import dayjs from 'dayjs';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useAuthUser } from 'auth/hooks';
import { Notification, SlidingPanel } from 'core/components';
import { ALL } from 'core/constants';
import { AgGridFilterOption, NotificationPosition, TableLayoutType } from 'core/enums';
import { AgGridServerTableProvider, NotificationProvider } from 'core/states';
import { AgGridGetRowsParams } from 'core/types';
import { IPricingProfile } from 'pricing/types';
import { quoteService } from 'quotes/services';
import { ICustomerQuote, IVendorQuote } from 'quotes/types';

import { columnDef } from './columns';
import { contentWrapperStyle } from './style';
import { getRowClass } from './table';
import { VendorQuoteListTable } from './VendorQuoteListTable';

interface IProps {
  isOpen: boolean;
  quote?: ICustomerQuote;
  pricingProfile?: IPricingProfile;
  onAttachVendorQuoteSuccess?: (attachedQuotes: IVendorQuote[]) => void;
  onClose: () => void;
}

export const AttachExistingVendorQuote = ({
  isOpen,
  quote,
  pricingProfile,
  onAttachVendorQuoteSuccess,
  onClose,
}: IProps) => {
  const { t } = useTranslation();

  const { user } = useAuthUser();

  const isVendorQuoteSelectable = (data: IVendorQuote): boolean =>
    data?.validUntil ? !dayjs(data?.validUntil).isBefore(new Date()) : true;

  const columnDefs = useMemo(() => columnDef(), []);

  const defaultColDef = useMemo(
    () => ({
      minWidth: 100,
      width: 150,
      maxWidth: 300,
    }),
    []
  );

  const initialState = useMemo(
    () => ({
      filter: {
        filterModel: {
          customerCorporateUserName: {
            filterType: 'text',
            type: AgGridFilterOption.CONTAINS,
            filter: user ? `${user.firstName} ${user.lastName}` : '',
          },
          validUntil: {
            filterType: 'date',
            type: AgGridFilterOption.GREATER_THAN,
            dateFrom: new Date().toISOString(),
          },
        },
      },
    }),
    [user]
  );

  const getRows = useCallback(
    ({ filters, ...rest }: AgGridGetRowsParams) =>
      quoteService.getVendorQuotes({
        filters: {
          ...filters,
          status: ALL,
          archived: 0,
        },
        ...rest,
      }),
    []
  );

  const getRowId = useCallback(({ data }) => data?.customId, []);
  const isRowSelectable = useCallback(({ data }) => isVendorQuoteSelectable(data), []);
  const getRowClassCallback = useCallback(({ data }) => getRowClass(data), []);

  return (
    <SlidingPanel
      isOpen={isOpen}
      onClosePanel={onClose}
      title={t('titleAttachExistingVendorQuote')}
      contentStyle={contentWrapperStyle}
      autoWidth
    >
      <AgGridServerTableProvider
        key={user?.id}
        tableId="quoteAttachExistingVendorQuoteTable"
        entityType="Quote"
        columnDefs={columnDefs}
        defaultColDef={defaultColDef}
        initialState={initialState}
        getRows={getRows}
        getRowId={getRowId}
        getRowClass={getRowClassCallback}
        isRowSelectable={isRowSelectable}
        layoutType={TableLayoutType.VENDOR_QUOTE_ATTACH_EXISTING}
        hasLayoutActions
        hasManageFilters
      >
        <NotificationProvider>
          <Notification position={NotificationPosition.DRAWER} />

          <VendorQuoteListTable
            quote={quote}
            pricingProfile={pricingProfile}
            onAttachVendorQuoteSuccess={onAttachVendorQuoteSuccess}
          />
        </NotificationProvider>
      </AgGridServerTableProvider>
    </SlidingPanel>
  );
};
